import {seatCardStamp} from "./cardMoves.js";
import {normalizeGamble} from "./gambleShow.js";

// The board's card show: a played card leaps off its player's coaster, hangs in the
// middle of the bar, then slides to every seat it touched. Everything the show decides —
// where the card goes, what each landing says, and which numbers wait for it — is
// answered here from the synced event log, so the board only paints it.

// How long a play stays worth showing. Longer than an ordinary show needs, because a
// chance card's suspense makes its show twice as long and the plays behind it wait.
export const SHOW_MAX_AGE_MS = 9000;

// Everything a card's own play records happens inside one synchronous playCard call,
// so it shares the card's timestamp to the millisecond. A turn-end ping or a turn-start
// drip that follows it in the log is somebody's later action, and this keeps it out.
export const PLAY_WINDOW_MS = 250;

// The tones a burst can paint. Anything else came off the network and is drawn as the
// quietest one rather than reaching a class name.
const TONES = new Set(["damage", "heal", "shield", "trick"]);

// What a card says when it lands back on its own player with nothing else to report.
const HOME_STAMPS = {heal: "Läker!", shield: "Skyddad!"};

function eventList(events) {
  return Array.isArray(events) ? events : [];
}

function amountOf(event) {
  const amount = Math.trunc(Number(event.amount));
  return amount > 0 ? amount : 0;
}

function sipWord(amount) {
  return amount === 1 ? "klunk" : "klunkar";
}

function findPlay(events, playId) {
  return eventList(events).findIndex((event) => event?.type === "card" && event.id === playId);
}

// The events a play caused: after it in the log, before the next card, inside the window.
export function playConsequences(events, playId) {
  const log = eventList(events);
  const start = findPlay(log, playId);
  if (start === -1) return [];
  const play = log[start];
  const caused = [];
  for (const event of log.slice(start + 1)) {
    if (!event) continue;
    if (event.type === "card" || !(event.at - play.at <= PLAY_WINDOW_MS)) break;
    caused.push(event);
  }
  return caused;
}

// The plays the board still owes the room: fresh ones it has not started, plus the one
// on screen until its card has landed. Their sips are held off the seats meanwhile.
export function heldPlayIds(events, seenIds, showing = null, now = Date.now(), maxAge = SHOW_MAX_AGE_MS) {
  const ids = eventList(events)
    .filter((event) => event?.type === "card" && now - event.at < maxAge && !seenIds.has(event.id))
    .map((event) => event.id);
  return showing && !showing.landed ? [...ids, showing.id] : ids;
}

// A guard that a held play knocked out keeps standing until that card arrives. Older
// states recorded no id, art or full health on the event, so each has a fallback.
function fallenGuard(event) {
  const hp = amountOf(event);
  return {
    id: String(event.minionId || event.id),
    name: String(event.minionName || ""),
    image: typeof event.minionImage === "string" ? event.minionImage : null,
    hp,
    maxHp: Math.max(hp, Number(event.minionMaxHp) || 0),
    fallen: true,
  };
}

// The table as the board shows it: the live seats with every held play taken back —
// its sips off the count, its new minions off the bar, its damage off the guards.
export function presentedSeats(players, events, heldIds) {
  const held = new Set(heldIds);
  const owed = new Map();
  const bruises = new Map();
  const fallen = new Map();
  held.forEach((playId) => playConsequences(events, playId).forEach((event) => {
    if (event.type === "minion" && event.killed) {
      fallen.set(event.playerId, [...(fallen.get(event.playerId) || []), fallenGuard(event)]);
    } else if (event.type === "minion") {
      bruises.set(event.minionId, (bruises.get(event.minionId) || 0) + amountOf(event));
    } else if (event.type === "drink" || event.type === "death") {
      owed.set(event.playerId, (owed.get(event.playerId) || 0) + amountOf(event));
    }
  }));
  return eventList(players).map((player) => {
    const sipsTaken = Math.max(0, player.sipsTaken - (owed.get(player.id) || 0));
    const standing = player.minions
      .filter((minion) => !held.has(minion.playId))
      .map((minion) => (bruises.has(minion.id)
        ? {...minion, hp: Math.min(minion.maxHp, minion.hp + bruises.get(minion.id))}
        : minion));
    return {sipsTaken, dead: sipsTaken >= player.maxSips, minions: [...(fallen.get(player.id) || []), ...standing]};
  });
}

function guardLine(guard) {
  return guard.killed ? `${guard.name} slogs ut` : `${guard.name} tog ${guard.amount}`;
}

function sourceLine(stop) {
  if (stop.selfInflicted) return `${sipWord(stop.sips)}, eget kort`;
  return stop.from ? `${sipWord(stop.sips)} från ${stop.from}` : sipWord(stop.sips);
}

// "3 × Underlägg" rather than the same name three times over.
function summonLine(names) {
  const counts = new Map();
  names.forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
  return [...counts].map(([name, count]) => (count > 1 ? `${count} × ${name}` : name)).join(", ");
}

// What lands on one seat, in the words the stamp prints. The worst news wins the big
// line: out, then sips, then a guard that took it, then a new minion, then a miss.
// Cards the play moved — drawn, taken, thrown away — come after all of that: as the big
// line when nothing else landed, otherwise as a line under it.
function stamp(stop, effect) {
  const guards = stop.guards.map(guardLine);
  const cards = stop.cards ? [stop.cards.big, ...stop.cards.lines] : [];
  if (stop.dead) return {tone: "damage", big: "Ute!", lines: [...guards, `${stop.sips} ${sipWord(stop.sips)} + en shot`, ...cards]};
  if (stop.sips) return {tone: "damage", big: `+${stop.sips}`, lines: [...guards, sourceLine(stop), ...cards]};
  if (guards.length) return {tone: "shield", big: "Skyddad!", lines: [...guards, ...cards]};
  if (stop.summoned.length) return {tone: effect, big: stop.home ? "Ny vakt!" : "Inflyttad!", lines: [summonLine(stop.summoned), ...cards]};
  if (stop.cards) return {tone: effect, big: stop.cards.big, lines: stop.cards.lines};
  if (stop.miss) return {tone: "trick", big: "Miss!", lines: []};
  return {tone: effect, big: stop.home ? HOME_STAMPS[effect] || "" : "", lines: []};
}

// The caption on the card's foot. The card already carries its own text, so this only
// says who threw it, and at whom when it was aimed.
function byline(play) {
  if (!play.playerName) return "";
  return play.targetName ? `${play.playerName} spelar mot ${play.targetName}` : `${play.playerName} spelar`;
}

// A chance card's moment, as the board plays it. The roulette's and the bottle's
// candidates become seats
// (-1 for one who has left, which the spotlight skips) and their picks their places in
// that list, which is what rouletteFrame lands on and the bottle turns to.
function gambleScript(play, seatOf) {
  const gamble = normalizeGamble(play.gamble);
  if (!gamble) return null;
  if (gamble.kind !== "roulette" && gamble.kind !== "bottle") return {...gamble, seats: [], landings: []};
  return {
    ...gamble,
    seats: gamble.candidateIds.map(seatOf),
    landings: gamble.pickIds.map((id) => gamble.candidateIds.indexOf(id)).filter((index) => index >= 0),
  };
}

// Fatigue is drunk at a turn start with no card to slide, so the board stamps it on the
// empty-handed player's mat instead: the fresh fatigue drinks it has not stamped yet.
export function fatigueStamps(events, players, seenIds, now = Date.now(), maxAge = SHOW_MAX_AGE_MS) {
  const seats = eventList(players);
  return eventList(events)
    .filter((event) => (event?.type === "drink" || event?.type === "death") && event.cause === "fatigue")
    .filter((event) => now - event.at < maxAge && !seenIds.has(event.id))
    .map((event) => ({event, playerIndex: seats.findIndex((player) => player.id === event.playerId)}))
    .filter(({playerIndex}) => playerIndex !== -1)
    .map(({event, playerIndex}) => {
      const amount = amountOf(event);
      const dead = event.type === "death";
      return {
        id: event.id,
        playerIndex,
        tone: "damage",
        big: dead ? "Ute!" : "Tom lek!",
        lines: dead ? ["tom lek", `${amount} ${sipWord(amount)} + en shot`] : [`+${amount} ${sipWord(amount)}`, "utmattning"],
        dead,
      };
    });
}

// One play's whole show: the card, who threw it, and every seat it slides to.
export function cardShowScript(events, playId, players = []) {
  const log = eventList(events);
  const play = log[findPlay(log, playId)];
  if (!play) return null;
  const seats = eventList(players);
  const seatOf = (id) => seats.findIndex((player) => player.id === id);
  const shooterIndex = seatOf(play.playerId);
  const effect = TONES.has(play.cardEffect) ? play.cardEffect : "trick";
  const stops = new Map();
  const stopAt = (playerIndex) => {
    if (!stops.has(playerIndex)) {
      stops.set(playerIndex, {playerIndex, home: playerIndex === shooterIndex, sips: 0, dead: false, guards: [], summoned: [], cards: null, miss: false});
    }
    return stops.get(playerIndex);
  };

  const moves = [];
  playConsequences(log, playId).forEach((event) => {
    if (event.type === "cards") {
      moves.push(...(Array.isArray(event.moves) ? event.moves : []));
      return;
    }
    const playerIndex = seatOf(event.playerId);
    if (playerIndex === -1) return;
    if (event.type === "minion") {
      stopAt(playerIndex).guards.push({id: event.minionId, name: String(event.minionName || ""), amount: amountOf(event), killed: Boolean(event.killed)});
    } else if (event.type === "drink" || event.type === "death") {
      const stop = stopAt(playerIndex);
      stop.sips += amountOf(event);
      stop.dead = stop.dead || event.type === "death";
      stop.from = event.sourceName;
      stop.selfInflicted = event.sourceId === event.playerId;
    }
  });
  // Every seat a card left or reached, the one it left first: a theft lands on its victim
  // before the thief's own mat.
  moves.flatMap((move) => [move?.fromId, move?.toId]).forEach((id) => {
    const playerIndex = seatOf(id);
    if (id && playerIndex !== -1) stopAt(playerIndex).cards = seatCardStamp(moves, id);
  });
  seats.forEach((player, playerIndex) => player.minions
    .filter((minion) => minion.playId === playId)
    .forEach((minion) => stopAt(playerIndex).summoned.push(minion.name)));
  const targetIndex = seatOf(play.targetId);
  // Aimed damage that touched nobody — an umbrella, an empty glass — still slides to
  // its target, so the room sees where it was meant to land.
  if (targetIndex !== -1 && !stops.has(targetIndex)) stopAt(targetIndex).miss = effect === "damage";
  if (!stops.size && shooterIndex !== -1) stopAt(shooterIndex);

  return {
    playId,
    shooterIndex,
    effect,
    image: typeof play.cardImage === "string" ? play.cardImage : null,
    name: String(play.cardName || "Kort"),
    tag: String(play.cardTag || "Kort"),
    text: String(play.cardText || ""),
    byline: byline(play),
    gamble: gambleScript(play, seatOf),
    stops: [...stops.values()].map((stop) => ({
      playerIndex: stop.playerIndex,
      home: stop.home,
      dead: stop.dead,
      fallen: stop.guards.filter((guard) => guard.killed).map((guard) => guard.id),
      ...stamp(stop, effect),
    })),
  };
}
